import Image from 'next/image';
import type { ReactNode } from 'react';
import type { FormProps } from 'tdesign-react';
import { Button, DialogPlugin, Form, MessagePlugin } from 'tdesign-react';
import { LanguageSwitchButton } from '../../language-switch-button';
import enLogo from './en-logo.png';
import type { Locale } from '../i18n/config';
import { getUploadPreviewUrl } from './AvatarUploadField';
import {
  BASIC_INFO_TITLE,
  CANCEL_BUTTON_TEXT,
  CHINESE_NAME_LABEL,
  CONTACT_ALLOW_TEXT,
  CONTACT_DECLINE_TEXT,
  CONTACT_PERMISSION_LABEL,
  COPYRIGHT_TEXT,
  EMAIL_LABEL,
  FIRST_NAME_LABEL,
  GENERAL_INPUT_PLACEHOLDER,
  HERO_SUBTITLE,
  HERO_TITLE,
  LAST_NAME_LABEL,
  MEETING_DATE_TEXT,
  MEETING_DESCRIPTION,
  MEETING_LOCATION_TEXT,
  MEETING_TITLE,
  NATIONALITY_LABEL,
  PERSONAL_PROFILE_LABEL,
  PUBLIC_DISPLAY_NOTICE_TEXT,
  SUCCESS_TITLE,
} from './config';
import desktopHeroIllustration from '../s-2026-signup/hero-right-illustration.png';
import {
  ContactPermissionField,
  createTextRules,
  DesktopAvatarField,
  DesktopBioFields,
  DesktopInputField,
  emailRules,
  TitleListFields,
} from './form-fields';
import {
  CalendarIcon,
  LanguageIcon,
  LocationIcon,
} from '../s-2026-signup/icons';
import { MeetingInfo, MeetingUserDropdown } from './shared';
import type { SignupFormValues } from './types';

const SUBMITTED_EMPTY_TEXT = '-';

const getSubmittedText = (value: unknown) => {
  const text = String(value ?? '').trim();
  return text || SUBMITTED_EMPTY_TEXT;
};

function SubmittedStatusIcon() {
  return (
    <span className="inline-flex h-16 w-16 items-center justify-center text-[#2BA471]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
      >
        <path
          d="M18 32.826L28 42.828L46 24.83L43.17 22L28 37.172L20.826 30L18 32.826Z"
          fill="#2BA471"
        />
        <path
          d="M16.444 8.71885C21.0486 5.64217 26.4621 4 32 4C39.4261 4 46.548 6.94999 51.799 12.201C57.05 17.452 60 24.5739 60 32C60 37.5379 58.3578 42.9514 55.2812 47.556C52.2045 52.1605 47.8315 55.7494 42.7151 57.8686C37.5988 59.9879 31.969 60.5424 26.5375 59.462C21.106 58.3816 16.1169 55.7149 12.201 51.799C8.28515 47.8831 5.61841 42.894 4.53802 37.4625C3.45764 32.0311 4.01213 26.4012 6.13139 21.2849C8.25064 16.1685 11.8395 11.7955 16.444 8.71885ZM18.6663 51.9553C22.6131 54.5924 27.2533 56 32 56C38.3652 56 44.4697 53.4714 48.9706 48.9706C53.4714 44.4697 56 38.3652 56 32C56 27.2532 54.5924 22.6131 51.9553 18.6663C49.3181 14.7195 45.5698 11.6434 41.1844 9.82689C36.799 8.01039 31.9734 7.53511 27.3178 8.46115C22.6623 9.3872 18.3859 11.673 15.0294 15.0294C11.673 18.3859 9.38721 22.6623 8.46117 27.3178C7.53512 31.9734 8.0104 36.799 9.8269 41.1844C11.6434 45.5698 14.7195 49.3181 18.6663 51.9553Z"
          fill="#2BA471"
        />
      </svg>
    </span>
  );
}

function SubmittedFieldBlock({
  label,
  children,
  widthClass = 'w-full',
  valueClassName = 'text-base leading-6 text-[rgba(0,0,0,0.9)] break-all',
}: {
  label: string;
  children: ReactNode;
  widthClass?: string;
  valueClassName?: string;
}) {
  return (
    <div className={`${widthClass} flex flex-col gap-2`}>
      <span className="text-sm leading-[22px] text-[rgba(0,0,0,0.4)]">
        {label}
      </span>
      <div className={valueClassName}>{children}</div>
    </div>
  );
}

function DesktopSubmittedDetail({
  values,
  showSuccessHeader = false,
  submitSuccessTitle = SUCCESS_TITLE,
}: {
  values: SignupFormValues;
  showSuccessHeader?: boolean;
  submitSuccessTitle?: string;
}) {
  const avatarUrl = getUploadPreviewUrl(values.avatar);
  const titleItems = values.titles.filter(
    (item) => item.titleCn.trim() || item.titleEn.trim()
  );

  return (
    <>
      {showSuccessHeader && (
        <div className="flex flex-col items-center gap-6">
          <SubmittedStatusIcon />
          <h1 className="text-[20px] font-semibold leading-[28px] text-[rgba(0,0,0,0.9)]">
            {submitSuccessTitle}
          </h1>
        </div>
      )}

      <div
        className={`flex flex-col gap-8 pb-6 ${
          showSuccessHeader ? 'mt-[72px]' : 'pt-10'
        }`}
      >
        <h2 className="text-[20px] leading-[28px] text-[rgba(0,0,0,0.9)]">
          {BASIC_INFO_TITLE}
        </h2>

        <div className="flex flex-col gap-8">
          <SubmittedFieldBlock label="Profile Picture" widthClass="w-[120px]">
            <div className="h-[120px] w-[120px] rounded-[3px] border border-dashed border-[#DCDCDC] bg-[#EEEEEE] p-2">
              {avatarUrl ? (
                <div
                  className="h-[104px] w-[104px] rounded-[3px] bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url(${avatarUrl})` }}
                />
              ) : null}
            </div>
          </SubmittedFieldBlock>

          <div className="flex gap-8">
            <SubmittedFieldBlock
              label={FIRST_NAME_LABEL}
              widthClass="w-[256px]"
            >
              {getSubmittedText(values.firstName)}
            </SubmittedFieldBlock>
            <SubmittedFieldBlock label={LAST_NAME_LABEL} widthClass="w-[256px]">
              {getSubmittedText(values.lastName)}
            </SubmittedFieldBlock>
            <SubmittedFieldBlock
              label={CHINESE_NAME_LABEL}
              widthClass="w-[256px]"
            >
              {getSubmittedText(values.nameCn)}
            </SubmittedFieldBlock>
          </div>

          <SubmittedFieldBlock label="Title">
            {titleItems.length ? (
              <div className="flex flex-col gap-1">
                {titleItems.map((item, index) => (
                  <div
                    key={`${item.titleEn}-${item.titleCn}-${index}`}
                    className="flex flex-wrap gap-x-6 gap-y-1"
                  >
                    <span className="text-base leading-6 text-[rgba(0,0,0,0.9)] break-all">
                      {getSubmittedText(item.titleEn)}
                    </span>
                    <span className="text-sm leading-[22px] text-[rgba(0,0,0,0.9)] break-all">
                      {getSubmittedText(item.titleCn)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              SUBMITTED_EMPTY_TEXT
            )}
          </SubmittedFieldBlock>

          <SubmittedFieldBlock label={NATIONALITY_LABEL}>
            <div className="text-base leading-6 text-[rgba(0,0,0,0.9)] break-all">
              {getSubmittedText(values.nationality)}
            </div>
          </SubmittedFieldBlock>
          <SubmittedFieldBlock label={EMAIL_LABEL}>
            <div className="text-base leading-6 text-[rgba(0,0,0,0.9)] break-all">
              {getSubmittedText(values.email)}
            </div>
          </SubmittedFieldBlock>
          <SubmittedFieldBlock label={PERSONAL_PROFILE_LABEL}>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-base leading-6 text-[rgba(0,0,0,0.9)] break-all">
                {getSubmittedText(values.bioEn)}
              </div>
              <div className="text-base leading-6 text-[rgba(0,0,0,0.9)] break-all">
                {getSubmittedText(values.bioCn)}
              </div>
            </div>
          </SubmittedFieldBlock>

          <SubmittedFieldBlock label={CONTACT_PERMISSION_LABEL}>
            {values.allowContact === 0
              ? CONTACT_DECLINE_TEXT
              : CONTACT_ALLOW_TEXT}
          </SubmittedFieldBlock>
        </div>
      </div>
    </>
  );
}

type DesktopSignupPageProps = {
  displayedAccount: string;
  enableLanguageSwitch: boolean;
  form: any;
  hasTeam: boolean;
  heroBackgroundSrc: string;
  isReadonlySubmittedState: boolean;
  isSubmitDisabled: boolean;
  isSubmitting: boolean;
  isSubmitSuccessState?: boolean; // 是否是从编辑/新增成功跳转过来的状态
  isEmailReadonly: boolean;
  langLabel: string;
  onCancel: () => void;
  onPrimaryAction: () => void;
  onSubmit: FormProps['onSubmit'];
  readonlyValues: SignupFormValues;
  submitButtonText: string;
  submitSuccessTitle?: string;
  syncSubmitState: (...args: any[]) => void;
  targetLang: Locale;
  teamType?: number;
};

export function DesktopSignupPage({
  displayedAccount,
  enableLanguageSwitch,
  form,
  hasTeam,
  heroBackgroundSrc,
  isReadonlySubmittedState,
  isSubmitDisabled,
  isSubmitting,
  isSubmitSuccessState = false,
  isEmailReadonly,
  langLabel,
  onCancel,
  onPrimaryAction,
  onSubmit,
  readonlyValues,
  submitButtonText,
  submitSuccessTitle,
  syncSubmitState,
  targetLang,
  teamType,
}: DesktopSignupPageProps) {
  const handleCancel = () => {
    const confirmDialog = DialogPlugin.confirm({
      header: 'Confirm',
      body: 'Are you sure you want to cancel? Your changes will not be saved.',
      confirmBtn: 'Confirm',
      cancelBtn: 'Cancel',
      onConfirm: () => {
        confirmDialog.hide();
        onCancel();
      },
      onCancel: () => {
        confirmDialog.hide();
      },
      onClose: () => {
        confirmDialog.hide();
      },
    });
  };

  return (
    <div className="signup-2026-desktop min-h-screen bg-[#F5F7FA] text-[#121212]">
      <section
        className="relative h-[320px] overflow-hidden bg-cover bg-center bg-no-repeat "
        style={{ backgroundImage: `url(${heroBackgroundSrc})` }}
      >
        <header className="fixed inset-x-0 top-0 z-30 h-14 border-b border-black/10 bg-white/60 backdrop-blur-[20px]">
          <div className="flex h-full items-center justify-between px-10">
            <div className="inline-flex items-center">
              <Image
                src={enLogo}
                alt="SSV 2026 logo"
                priority
                sizes="180px"
                className="h-[29px] w-[180px] object-contain"
              />
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitchButton
                targetLang={targetLang}
                className="inline-flex items-center gap-1 rounded-full border border-[#2C2C2C] bg-white/50 px-3 py-[5px] text-sm leading-[22px] text-[#2C2C2C]"
                onClick={(event) => {
                  if (enableLanguageSwitch) return;
                  event.preventDefault();
                  MessagePlugin.warning(
                    'Language switching is currently unavailable.'
                  );
                }}
              >
                <LanguageIcon />
                <span>{langLabel}</span>
              </LanguageSwitchButton>
              <MeetingUserDropdown
                displayedAccount={displayedAccount}
                hasTeam={hasTeam}
                teamType={teamType}
              />
            </div>
          </div>
        </header>

        <div className="relative mx-auto flex h-full w-[832px] items-center pt-14">
          <div className="flex shrink-0 flex-col gap-9">
            <div className="flex flex-col gap-2">
              <p className="text-[28px] font-semibold leading-[36px] text-[#003CAB]">
                {HERO_TITLE}
              </p>
              <p className="text-[20px] font-semibold leading-[28px] text-[#003CAB]">
                {HERO_SUBTITLE}
              </p>
            </div>
            <div className="flex items-center gap-8 text-sm leading-[22px] text-[#121212]">
              <MeetingInfo
                className="gap-3"
                icon={
                  <span className="text-[#B5C7FF]">
                    <CalendarIcon />
                  </span>
                }
              >
                {MEETING_DATE_TEXT}
              </MeetingInfo>
              <MeetingInfo
                className="gap-3"
                icon={
                  <span className="text-[#B5C7FF]">
                    <LocationIcon />
                  </span>
                }
              >
                {MEETING_LOCATION_TEXT}
              </MeetingInfo>
            </div>
          </div>
          <Image
            src={desktopHeroIllustration}
            alt="SSV 2026 hero illustration"
            priority
            sizes="253px"
            className="relative left-[217px] [278px] w-[253px] shrink-0 object-contain"
          />
        </div>
      </section>

      <main className="bg-white">
        <div className="mx-auto w-[832px]">
          {isReadonlySubmittedState ? (
            <div className="mx-auto flex w-[832px] flex-col pt-10">
              <DesktopSubmittedDetail
                values={readonlyValues}
                showSuccessHeader={isSubmitSuccessState}
                submitSuccessTitle={submitSuccessTitle}
              />
            </div>
          ) : (
            <div className="mx-auto flex w-[832px] flex-col gap-10 pt-[50px]">
              <div className="flex flex-col gap-6">
                <h1 className="text-[20px] font-semibold leading-[28px] text-[rgba(0,0,0,0.9)]">
                  {MEETING_TITLE}
                </h1>
                <p className="text-base leading-[24px] text-[rgba(0,0,0,0.9)]">
                  {MEETING_DESCRIPTION}
                </p>
              </div>

              <div className="flex flex-col gap-6">
                {/* 隐私提示：提交的信息将在官网和社交功能中公开展示 */}
                <div className="flex items-center gap-3 rounded-[4px] bg-[#E9EEFF] px-4 py-3">
                  <span className="shrink-0 text-[#2670F2]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <path
                        d="M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.3181 11.6819 1.33337 8 1.33337C4.3181 1.33337 1.33337 4.3181 1.33337 8C1.33337 11.6819 4.3181 14.6667 8 14.6667Z"
                        fill="#2670F2"
                      />
                      <path
                        d="M8.00004 10.6667V8"
                        stroke="#fff"
                        strokeWidth="1.33333"
                        strokeLinecap="round"
                      />
                      <path
                        d="M8.00004 5.33337V5.34004"
                        stroke="#fff"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  <span className="text-sm leading-[22px] text-[rgba(0,0,0,0.9)]">
                    {PUBLIC_DISPLAY_NOTICE_TEXT}
                  </span>
                </div>

                <h2 className="text-[20px] leading-[28px] text-[rgba(0,0,0,0.9)]">
                  {BASIC_INFO_TITLE}
                </h2>

                <Form
                  className="flex flex-col"
                  form={form}
                  initialData={readonlyValues}
                  labelAlign="top"
                  layout="vertical"
                  preventSubmitDefault
                  requiredMark={false}
                  scrollToFirstError="smooth"
                  showErrorMessage
                  statusIcon={false}
                  submitWithWarningMessage={false}
                  onSubmit={onSubmit}
                  onValuesChange={syncSubmitState}
                >
                  <DesktopAvatarField
                    onUploadStateChange={syncSubmitState}
                    disabled={isReadonlySubmittedState}
                  />

                  <div className="flex gap-8 pb-6">
                    <div className="w-[256px]">
                      <DesktopInputField
                        label={FIRST_NAME_LABEL}
                        name="firstName"
                        placeholder={GENERAL_INPUT_PLACEHOLDER}
                        required
                        rules={createTextRules({
                          maxLength: 50,
                        })}
                        maxlength={50}
                        disabled={isReadonlySubmittedState}
                      />
                    </div>
                    <div className="w-[256px]">
                      <DesktopInputField
                        label={LAST_NAME_LABEL}
                        name="lastName"
                        placeholder={GENERAL_INPUT_PLACEHOLDER}
                        required
                        rules={createTextRules({
                          maxLength: 50,
                        })}
                        maxlength={50}
                        disabled={isReadonlySubmittedState}
                      />
                    </div>
                    <div className="w-[256px]">
                      <DesktopInputField
                        label={CHINESE_NAME_LABEL}
                        name="nameCn"
                        placeholder={GENERAL_INPUT_PLACEHOLDER}
                        rules={createTextRules({
                          maxLength: 20,
                          required: false,
                        })}
                        maxlength={20}
                        disabled={isReadonlySubmittedState}
                      />
                    </div>
                  </div>

                  <TitleListFields
                    form={form}
                    disabled={isReadonlySubmittedState}
                  />

                  <div className="flex gap-8 pb-6">
                    <div className="w-[400px]">
                      <DesktopInputField
                        label={NATIONALITY_LABEL}
                        name="nationality"
                        placeholder={GENERAL_INPUT_PLACEHOLDER}
                        rules={createTextRules({
                          maxLength: 100,
                          required: false,
                        })}
                        maxlength={100}
                        disabled={isReadonlySubmittedState}
                      />
                    </div>
                    <div className="w-[400px]">
                      <DesktopInputField
                        label={EMAIL_LABEL}
                        name="email"
                        placeholder={GENERAL_INPUT_PLACEHOLDER}
                        required
                        rules={emailRules}
                        maxlength={100}
                        disabled={isReadonlySubmittedState || isEmailReadonly}
                      />
                    </div>
                  </div>

                  <DesktopBioFields disabled={isReadonlySubmittedState} />

                  <div className="pb-6">
                    <ContactPermissionField
                      desktop
                      disabled={isReadonlySubmittedState}
                    />
                  </div>
                </Form>
              </div>
            </div>
          )}
        </div>

        <footer className="mt-8 pb-4 pt-2">
          <div className="flex flex-col gap-8">
            <div className="rounded-b-[6px] bg-[#F3F3F3] px-[164px] py-8">
              <div className="mx-auto flex w-[832px] items-center justify-center gap-2">
                <Button
                  size="medium"
                  theme="primary"
                  variant="base"
                  className="!h-8 !rounded-[3px] !px-4 !text-sm !font-normal !leading-[22px]"
                  loading={isSubmitting}
                  disabled={isSubmitDisabled}
                  onClick={onPrimaryAction}
                >
                  {submitButtonText}
                </Button>
                <Button
                  size="medium"
                  theme="default"
                  variant="base"
                  className={`!h-8 !rounded-[3px] !border-transparent !bg-[#E7E7E7] !px-4 !text-sm !font-normal !leading-[22px] !text-[rgba(0,0,0,0.9)] hover:!bg-[#DCDCDC] ${
                    isReadonlySubmittedState ? '!hidden' : ''
                  }`}
                  disabled={isSubmitting}
                  onClick={handleCancel}
                >
                  {CANCEL_BUTTON_TEXT}
                </Button>
              </div>
            </div>
            <p className="text-center text-sm leading-[22px] text-[rgba(0,0,0,0.4)]">
              {COPYRIGHT_TEXT}
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
